
import React, { useState } from 'react';
import Modal from '../ui/Modal';
import { formatDateRange, formatDateYYYYMMDD, getWeekStartDate } from '../../utils/dateUtils';
import { toast } from 'react-hot-toast';

interface RepeatScheduleModalProps {
    sourceWeekStart: Date;
    onClose: () => void;
    onRepeat: (repeatStartDate: Date, repeatEndDate: Date) => void;
}

const RepeatScheduleModal: React.FC<RepeatScheduleModalProps> = ({ sourceWeekStart, onClose, onRepeat }) => {
    const nextWeekStart = new Date(sourceWeekStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);

    const twoWeeksLater = new Date(nextWeekStart);
    twoWeeksLater.setDate(twoWeeksLater.getDate() + 13); // End of the second week

    const [startDate, setStartDate] = useState(formatDateYYYYMMDD(nextWeekStart));
    const [endDate, setEndDate] = useState(formatDateYYYYMMDD(twoWeeksLater));

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        // Basic validation
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            toast.error('Please enter valid start and end dates.');
            return;
        }
        if (end < start) {
            toast.error('The end date cannot be before the start date.');
            return;
        }
        if (start < sourceWeekStart) {
            toast.error('The repeat period cannot start in the past or during the source week.');
            return;
        }

        onRepeat(start, end);
    };

    return (
        <Modal title="Repeat Weekly Schedule" onClose={onClose}>
            <form onSubmit={handleSubmit}>
                <div className="p-6 space-y-4">
                    <div className="p-3 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-700 rounded-md text-center">
                        <p className="text-sm text-gray-600 dark:text-gray-300">You are copying the schedule from:</p>
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{formatDateRange(sourceWeekStart)}</p>
                    </div>

                    <div>
                        <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Repeat starting from:
                        </label>
                        <input
                            type="date"
                            id="start-date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            required
                            className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200"
                        />
                    </div>
                     <div>
                        <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Repeat until (inclusive):
                        </label>
                        <input
                            type="date"
                            id="end-date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            required
                            className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200"
                        />
                    </div>
                     <p className="text-xs text-gray-500 dark:text-gray-400">
                        This action will overwrite any existing shifts for the selected staff and date range with the schedule from the source week.
                    </p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t dark:border-slate-700">
                    <button
                        type="submit"
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                        Apply Repetition
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default RepeatScheduleModal;